import React, { useState, useEffect, useCallback } from 'react';
import { getSession, onAuthChange, mesmaPessoa } from './data/auth';
import FIVSystem from './components/FIVSystem';
import AreaCliente from './components/AreaCliente';
import AreaTecnico from './components/AreaTecnico';
import TelaDeSenha from './components/TelaDeSenha';
import LoginScreen from './components/LoginScreen';
import PainelAdmin from './components/Admin/PainelAdmin';
import NaoEncontrado from './components/Admin/NaoEncontrado';
import { souSuperAdmin } from './data/admin';
import {
  meuCadastro, tokenDaUrl, situacaoDoLink,
  sessaoGuardada, guardarSessao, esquecerSessao,
} from './data/cliente';
import { quemEntrouPeloLink } from './data/tecnico';

/**
 * A rota /admin, lida do caminho da URL.
 *
 * Sem router: o app já roteia assim (o link do cliente vem de `?acesso=`), e
 * uma dependência nova para uma rota só não se paga.
 *
 * A barra final é tolerada porque quem digita à mão às vezes a inclui.
 */
const ehRotaAdmin = () =>
  typeof window !== 'undefined'
  && window.location.pathname.replace(/\/+$/, '').toLowerCase().endsWith('/admin');

function App() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cadastro de cliente de quem entrou; nulo para quem é do laboratório.
  const [cadastroCliente, setCadastroCliente] = useState(null);
  const [verificandoCliente, setVerificandoCliente] = useState(false);

  // Link de acompanhamento. Lido uma vez: se ficasse sendo relido a cada
  // render, mudar a URL no meio da sessão trocaria a tela sob o usuário.
  const [token] = useState(() => tokenDaUrl());

  // Mesma razão para a rota: lida uma vez.
  const [rotaAdmin] = useState(ehRotaAdmin);
  // null = ainda verificando. A guarda é conforto; quem barra de verdade é a
  // RLS, que devolve vazio para quem não tem o acesso.
  const [superAdmin, setSuperAdmin] = useState(null);
  const [fase, setFase] = useState(() => (tokenDaUrl() ? 'verificando' : 'sem-link'));
  const [sessao, setSessao] = useState(null);
  // `{ tipo: 'cliente' | 'tecnico', cadastro }`: o mesmo link serve aos dois,
  // e só depois da senha se sabe de quem ele é.
  const [cadastroPorLink, setCadastroPorLink] = useState(null);
  const [laboratorio, setLaboratorio] = useState(null);
  const [bloqueadoAte, setBloqueadoAte] = useState(null);

  useEffect(() => {
    /**
     * Mesma pessoa, MESMO objeto.
     *
     * O Supabase emite um `user` novo a cada renovação de token e a cada vez
     * que a aba volta ao foco. Trocar o objeto por outro equivalente fazia
     * todo `useEffect([user])` da árvore disparar de novo — e um deles
     * (`verificandoCliente`) devolve a tela de carregamento, que DESMONTA o
     * FIVSystem inteiro. O efeito na prática era o app "recarregando" sozinho
     * no meio do trabalho, mostrando "Verificando acesso do usuário".
     *
     * Guardar o objeto anterior quando o id é o mesmo corta o problema na
     * raiz, em vez de caçar cada consumidor.
     */
    const aplicarSessao = (session) => {
      setSession(session);
      setUser(prev => mesmaPessoa(prev, session?.user));
      setLoading(false);
    };

    // Verificar sessão atual
    getSession().then(aplicarSessao);

    // Escutar mudanças na autenticação
    const { data: { subscription } } = onAuthChange((_event, session) => {
      aplicarSessao(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Confere o acesso de plataforma, e só em /admin.
   *
   * Sem cache de propósito (`souSuperAdmin` consulta direto): decidir permissão
   * a partir de cópia local é decidir com dado velho. Qualquer falha cai em
   * "não é" — na dúvida, a porta fica fechada.
   */
  useEffect(() => {
    if (!rotaAdmin) return;
    if (!user) { setSuperAdmin(null); return; }

    let cancelado = false;
    souSuperAdmin()
      .then(v => { if (!cancelado) setSuperAdmin(v); })
      .catch(() => { if (!cancelado) setSuperAdmin(false); });
    return () => { cancelado = true; };
  }, [rotaAdmin, user]);

  /**
   * O link é resolvido ANTES de qualquer coisa de sessão.
   *
   * Quem chega por link é anônimo: sem isto, ele veria a tela de login do
   * laboratório, que é exatamente o que o link existe para evitar.
   *
   * A sessão guardada é testada primeiro. Quem já entrou uma vez neste aparelho
   * não digita senha de novo por 30 dias — do contrário o link deixaria de ser
   * conveniente, e conveniência é a razão dele existir.
   */
  useEffect(() => {
    if (!token) return;
    let cancelado = false;

    (async () => {
      const guardada = sessaoGuardada(token);
      if (guardada) {
        try {
          const c = await quemEntrouPeloLink(guardada);
          if (cancelado) return;
          if (c) {
            setSessao(guardada);
            setCadastroPorLink(c);
            setFase('dentro');
            return;
          }
        } catch { /* sessão morta ou rede ruim: cai para a senha */ }
        // Sessão vencida, ou link revogado por baixo dela. Apagar evita tentar
        // de novo a cada abertura com uma credencial que já não vale.
        esquecerSessao(token);
        if (cancelado) return;
      }

      try {
        const s = await situacaoDoLink(token);
        if (cancelado) return;
        setLaboratorio(s.laboratorio || null);
        setBloqueadoAte(s.liberado_em || null);
        // 'bloqueado' também vai para a tela de senha: é lá que a espera é
        // explicada e contada, e a pessoa já fica no lugar certo quando liberar.
        setFase(s.situacao === 'invalido' ? 'invalido' : 'senha');
      } catch {
        if (!cancelado) setFase('invalido');
      }
    })();

    return () => { cancelado = true; };
  }, [token]);

  const entrouComSenha = useCallback(async (novaSessao) => {
    guardarSessao(token, novaSessao);
    setSessao(novaSessao);
    try {
      const quem = await quemEntrouPeloLink(novaSessao);
      if (!quem) throw new Error('link sem dono');
      setCadastroPorLink(quem);
      setFase('dentro');
    } catch {
      setFase('invalido');
    }
  }, [token]);

  const sairDoLink = useCallback(() => {
    esquecerSessao(token);
    setSessao(null);
    setCadastroPorLink(null);
    setBloqueadoAte(null);
    setFase('senha');
  }, [token]);

  /**
   * Descobre se quem entrou é cliente antes de montar qualquer tela.
   *
   * Sem esta espera, o cliente veria por um instante o app do laboratório —
   * que só falharia depois, ao não achar laboratório nenhum. Piscar a tela
   * errada é pior que esperar.
   */
  useEffect(() => {
    if (!user) { setCadastroCliente(null); return; }

    let cancelado = false;
    setVerificandoCliente(true);
    meuCadastro()
      .then(c => { if (!cancelado) setCadastroCliente(c); })
      .catch(() => { if (!cancelado) setCadastroCliente(null); })
      .finally(() => { if (!cancelado) setVerificandoCliente(false); });

    return () => { cancelado = true; };
    // O id, e não o objeto: mesmo com a identidade estabilizada acima, é o id
    // que define se a pergunta "quem é este?" precisa ser refeita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const carregando = (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-slate-600">Carregando...</p>
      </div>
    </div>
  );

  /**
   * /admin sai antes de tudo.
   *
   * Antes do ramo do link de cliente e fora do FIVSystem: lá dentro,
   * `useLicenca` exige vínculo em `lab_members` e bloquearia a própria tela de
   * suporte num laboratório onde o super admin não é membro.
   *
   * Sem sessão vai para o login — o super admin precisa poder entrar digitando
   * /admin direto. Qualquer outro desfecho é "página não encontrada": dizer
   * "acesso negado" confirmaria que existe um painel ali.
   */
  if (rotaAdmin) {
    if (loading) return carregando;
    if (!session) return <div className="App"><LoginScreen /></div>;
    if (superAdmin === null) return carregando;
    if (!superAdmin) return <NaoEncontrado />;
    return <PainelAdmin user={user} />;
  }

  if (fase === 'verificando' || loading || (user && verificandoCliente)) {
    return carregando;
  }

  if (fase === 'senha') {
    return (
      <div className="App">
        <TelaDeSenha
          token={token}
          laboratorio={laboratorio}
          bloqueadoAte={bloqueadoAte}
          onEntrou={entrouComSenha}
        />
      </div>
    );
  }

  if (fase === 'invalido') {
    /* Link vencido ou revogado é caso esperado, não erro: o texto diz o que
       fazer em vez de mandar procurar suporte. */
    return (
      <div className="App min-h-[100dvh] bg-slate-50 flex items-center justify-center px-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-sm text-center">
          <p className="font-medium text-slate-900">Link indisponível</p>
          <p className="text-sm text-slate-600 mt-1">
            Este link de acompanhamento expirou ou foi substituído.
            Peça um novo ao laboratório.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {fase === 'dentro'
        ? (cadastroPorLink?.tipo === 'tecnico'
          ? <AreaTecnico cadastro={cadastroPorLink.cadastro} sessao={sessao} onSair={sairDoLink} />
          : <AreaCliente cadastro={cadastroPorLink?.cadastro} sessao={sessao} onSair={sairDoLink} />)
        : !session ? <LoginScreen />
        : cadastroCliente ? <AreaCliente cadastro={cadastroCliente} />
        : <FIVSystem user={user} />}
    </div>
  );
}

export default App;
